<!-- loginPage.vue -->
<template>
  <el-container class="login-container">
    <el-header class="header">
      <div class="header-content">
        <div class="logo">
          <span>QwQ</span>
        </div>
        <div class="header-title">
          <span class="welcome-text">欢迎登录</span>
        </div>
      </div>
    </el-header>
    
    <el-main class="content" v-loading="loading">
      <!-- 浮动装饰气泡 -->
      <div class="floating-bubbles">
        <span class="bubble" v-for="i in 8" :key="i" :style="{ '--i': i, '--delay': i * 0.6 + 's', '--size': (18 + Math.random() * 35) + 'px' }"></span>
      </div>

      <div class="login-card">
        <div class="card-header">
          <div class="avatar-wrapper">
            <div class="avatar-ring">
              <div class="avatar-emoji">😊</div>
            </div>
          </div>
          <h1 class="form-title">账户登录</h1>
          <p class="form-subtitle">
            <span class="typing-text">请输入用户名和密码</span>
          </p>
        </div>
        
        <el-form 
          :model="ruleForm" 
          status-icon 
          :rules="rules" 
          ref="ruleForm" 
          label-width=""
          class="login-form"
        >
          <el-form-item prop="username">
            <div class="input-with-icon" :class="{ 'input-focused': usernameFocus }">
              <i class="el-icon-user input-icon"></i>
              <el-input 
                maxlength="15" 
                show-word-limit 
                placeholder="用户名" 
                v-model="ruleForm.username"
                class="styled-input"
                @focus="usernameFocus = true"
                @blur="usernameFocus = false"
              />
              <span class="input-emoji" v-if="ruleForm.username.length > 0">✅</span>
            </div>
          </el-form-item>
          
          <el-form-item prop="password">
            <div class="input-with-icon" :class="{ 'input-focused': passwordFocus }">
              <i class="el-icon-lock input-icon"></i>
              <el-input 
                type="password" 
                maxlength="18" 
                placeholder="密码" 
                v-model="ruleForm.password" 
                autocomplete="off"
                class="styled-input"
                @focus="passwordFocus = true"
                @blur="passwordFocus = false"
                @keyup.enter="submitForm('ruleForm')"
              />
              <span class="input-emoji" v-if="ruleForm.password.length > 0">🔒</span>
            </div>
          </el-form-item>
          
          <el-form-item class="form-actions">
            <el-button 
              type="primary" 
              @click="submitForm('ruleForm')" 
              class="login-btn"
              :loading="loading"
            >
              <span v-if="!loading">
                <span class="btn-icon">🚀</span> 登录
              </span>
              <span v-else>
                <span class="btn-spinner"></span> 登录中...
              </span>
            </el-button>
            
            <div class="btn-divider">
              <span class="divider-line"></span>
              <span class="divider-text">或</span>
              <span class="divider-line"></span>
            </div>
            
            <el-button 
              @click="resetForm('ruleForm')" 
              class="reset-btn"
            >
              <span class="btn-icon">🔄</span> 重置
            </el-button>
          </el-form-item>
        </el-form>
        
        <div class="card-footer">
          <div class="security-info">
            <span class="pulse-dot"></span>
            <span>安全加密登录</span>
          </div>
        </div>
      </div>
    </el-main>
    
    <el-footer class="footer">
      <div class="footer-content">
        <span class="author">© 2025 由 <a href="http://askook.cn">Askook</a> 设计开发</span>
        <span class="footer-emoji">❤️</span>
      </div>
    </el-footer>
  </el-container>
</template>

<script>
export default {
  name: 'App',
  props: ['baseurl'],
  data() {
    var validateUsername = (rule, value, callback) => {
      if (!value) {
        callback(new Error('用户名不能为空'));
        return;
      }
      const reg = /^[A-Za-z0-9]+$/;
      if (!reg.test(value)) {
        callback(new Error('用户名格式有误（只能包含字母和数字）'));
      } else if (value.length > 15 || value.length < 5) {
        callback(new Error('用户名长度应在5-15个字符之间'));
      } else {
        callback();
      }
    };
    
    var validatePassword = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入密码'));
        return;
      }
      if (value.length > 16 || value.length < 6) {
        callback(new Error('密码长度应在6-16个字符之间'));
      } else {
        callback();
      }
    };
    
    return {
      loading: false,
      usernameFocus: false,
      passwordFocus: false,
      ruleForm: {
        password: '',
        username: ''
      },
      rules: {
        password: [
          { validator: validatePassword, trigger: 'blur' }
        ],
        username: [
          { validator: validateUsername, trigger: 'blur' }
        ]
      }
    };
  },
  methods: {
    async submitForm(formName) {
      try {
        const valid = await this.$refs[formName].validate();
        if (!valid) return;
      } catch (error) {
        console.log('表单验证失败:', error);
        return;
      }
      
      this.loading = true;
      
      try {
        const response = await fetch(`${this.baseurl}/login`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({ 
            username: this.ruleForm.username,
            password: this.ruleForm.password
          })
        });
        
        if (!response.ok) {
          throw new Error(`HTTP错误: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.success) {
          this.$message({
            message: data.message || '登录成功 🎉',
            type: 'success',
            duration: 2000
          });
          if (data.user) {
            localStorage.setItem('user', JSON.stringify(data.user));
          }
          this.$emit('login-success');
        } else {
          this.$message.error(data.message || '登录失败，请重试 😅');
        }
      } catch (error) {
        console.error('登录错误:', error);
        this.$message.error('登录失败: ' + error.message);
      } finally {
        this.loading = false;
      }
    },
    
    resetForm(formName) {
      this.$refs[formName].resetFields();
      this.$message({
        message: '已重置 ✨',
        type: 'info',
        duration: 1500
      });
    }
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.login-container {
  height: 100vh;
  width: 100%;
  position: relative;
  overflow: hidden;
  font-family: 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  background: linear-gradient(145deg, #e8f4fd 0%, #d4e8fa 40%, #b8d8f5 100%);
  display: flex;
  flex-direction: column;
}

/* ===== 浮动装饰气泡 ===== */
.floating-bubbles {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 1;
  pointer-events: none;
  overflow: hidden;
}

.bubble {
  position: absolute;
  bottom: -60px;
  width: var(--size, 25px);
  height: var(--size, 25px);
  background: radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.05));
  border-radius: 50%;
  animation: bubbleFloat 14s ease-in-out infinite;
  animation-delay: var(--delay, 0s);
  left: calc(5% + (var(--i) * 12%));
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.bubble::before {
  content: '';
  position: absolute;
  top: 18%;
  left: 22%;
  width: 28%;
  height: 22%;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 50%;
  transform: rotate(-30deg);
}

@keyframes bubbleFloat {
  0% {
    transform: translateY(0) scale(1) rotate(0deg);
    opacity: 0;
  }
  8% {
    opacity: 0.8;
  }
  92% {
    opacity: 0.8;
  }
  100% {
    transform: translateY(-115vh) scale(1.3) rotate(720deg);
    opacity: 0;
  }
}

/* ===== 头部样式 ===== */
.header {
  height: 60px;
  padding: 0 24px;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  z-index: 2;
  position: relative;
  flex-shrink: 0;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
}

.logo {
  display: flex;
  align-items: center;
  color: #1677ff;
  font-size: 16px;
  font-weight: 600;
}

.logo-icon {
  font-size: 22px;
  margin-right: 8px;
  animation: logoSpin 8s ease-in-out infinite;
}

@keyframes logoSpin {
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(12deg); }
  75% { transform: rotate(-12deg); }
}

.header-title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #4e5969;
  font-size: 14px;
}

.waving-hand {
  display: inline-block;
  animation: wave 2.5s ease-in-out infinite;
  transform-origin: 70% 70%;
}

@keyframes wave {
  0%, 100% { transform: rotate(0deg); }
  10% { transform: rotate(14deg); }
  20% { transform: rotate(-8deg); }
  30% { transform: rotate(14deg); }
  40% { transform: rotate(-4deg); }
  50% { transform: rotate(10deg); }
  60% { transform: rotate(0deg); }
}

/* ===== 内容区域 ===== */
.content {
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  z-index: 2;
  position: relative;
}

/* ===== 登录卡片 ===== */
.login-card {
  width: 100%;
  max-width: 400px;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 36px 30px 28px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  box-shadow: 
    0 8px 40px rgba(22, 119, 255, 0.10),
    0 2px 8px rgba(0, 0, 0, 0.04);
  animation: cardSlideUp 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  position: relative;
  overflow: hidden;
}

.login-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 300%;
  height: 3px;
  background: linear-gradient(90deg, transparent, #1677ff, #69b1ff, #1677ff, transparent);
  animation: shimmerLine 4s ease-in-out infinite;
}

@keyframes shimmerLine {
  0% { transform: translateX(0); }
  100% { transform: translateX(33%); }
}

@keyframes cardSlideUp {
  from {
    opacity: 0;
    transform: translateY(40px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* ===== 卡片头部 ===== */
.card-header {
  margin-bottom: 28px;
  text-align: center;
}

.avatar-wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.avatar-ring {
  width: 76px;
  height: 76px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e3f0ff, #d4e8ff);
  border: 2px solid rgba(22, 119, 255, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ringPulse 3s ease-in-out infinite;
  position: relative;
}

.avatar-ring::before {
  content: '';
  position: absolute;
  inset: -4px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(22, 119, 255, 0.15), rgba(105, 177, 255, 0.15));
  animation: ringPulse 3s ease-in-out infinite 0.5s;
}

@keyframes ringPulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(22, 119, 255, 0.08);
  }
  50% {
    transform: scale(1.04);
    box-shadow: 0 0 30px 6px rgba(22, 119, 255, 0.06);
  }
}

.avatar-emoji {
  font-size: 34px;
  z-index: 1;
  animation: avatarBounce 4s ease-in-out infinite;
}

@keyframes avatarBounce {
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-6px) scale(1.02); }
}

.form-title {
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 4px;
  color: #1d2129;
  letter-spacing: 0.5px;
}

.form-title-gradient {
  background: linear-gradient(135deg, #1677ff, #4096ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.form-subtitle {
  font-size: 14px;
  color: #86909c;
}

.typing-text {
  display: inline-block;
  animation: typingPulse 2s ease-in-out infinite;
}

@keyframes typingPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* ===== 表单样式 ===== */
.login-form {
  position: relative;
}

.input-with-icon {
  position: relative;
  margin-bottom: 16px;
  transition: all 0.3s ease;
}

.input-with-icon.input-focused {
  transform: translateY(-1px);
}

.input-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #86909c;
  font-size: 15px;
  z-index: 3;
  transition: all 0.3s ease;
}

.input-focused .input-icon {
  color: #1677ff;
  transform: translateY(-50%) scale(1.1);
}

.input-emoji {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 15px;
  z-index: 3;
  animation: popIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes popIn {
  from {
    opacity: 0;
    transform: translateY(-50%) scale(0.5);
  }
  to {
    opacity: 1;
    transform: translateY(-50%) scale(1);
  }
}

.styled-input >>> .el-input__inner {
  padding-left: 42px;
  padding-right: 38px;
  height: 46px;
  border-radius: 12px;
  border: 1.5px solid rgba(229, 230, 235, 0.6);
  background: rgba(255, 255, 255, 0.85);
  font-size: 14px;
  transition: all 0.3s ease;
  color: #1d2129;
}

.styled-input >>> .el-input__inner:hover {
  border-color: #b8d0e8;
  background: #fff;
}

.styled-input >>> .el-input__inner:focus {
  border-color: #1677ff;
  box-shadow: 0 0 0 4px rgba(22, 119, 255, 0.08);
  background: #fff;
}

.styled-input >>> .el-input__count {
  color: #86909c;
  background: transparent;
  font-size: 12px;
  padding-right: 4px;
}

/* ===== 表单操作按钮 ===== */
.form-actions {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.login-btn {
  width: 100%;
  height: 46px;
  border-radius: 12px;
  background: linear-gradient(135deg, #1677ff, #4096ff);
  border: none;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0.5px;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  overflow: hidden;
}

.login-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 200%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.12), transparent);
  animation: btnShine 3s ease-in-out infinite;
}

@keyframes btnShine {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

.login-btn:hover {
  transform: translateY(-2px) scale(1.01);
  box-shadow: 0 8px 28px rgba(22, 119, 255, 0.30);
}

.login-btn:active {
  transform: translateY(0) scale(0.98);
}

.login-btn .btn-icon {
  display: inline-block;
  margin-right: 6px;
}

.login-btn:hover .btn-icon {
  animation: btnIconBounce 0.6s ease;
}

@keyframes btnIconBounce {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px) rotate(-8deg); }
  75% { transform: translateX(5px) rotate(8deg); }
}

/* 按钮分隔线 */
.btn-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 0 8px;
}

.divider-line {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(22, 119, 255, 0.12), transparent);
}

.divider-text {
  font-size: 12px;
  color: #a8b5c8;
  font-weight: 400;
  white-space: nowrap;
  letter-spacing: 0.5px;
}

/* 重置按钮 */
.reset-btn {
  width: 100%;
  height: 46px;
  border-radius: 12px;
  font-size: 15px;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  border: 1.5px solid rgba(229, 230, 235, 0.5);
  color: #4e5969;
  background: rgba(255, 255, 255, 0.6);
}

.reset-btn:hover {
  border-color: #1677ff;
  color: #1677ff;
  background: rgba(22, 119, 255, 0.04);
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(22, 119, 255, 0.08);
}

.reset-btn:active {
  transform: translateY(0) scale(0.98);
}

.reset-btn .btn-icon {
  display: inline-block;
  margin-right: 6px;
}

.reset-btn:hover .btn-icon {
  animation: resetSpin 0.6s ease;
}

@keyframes resetSpin {
  to { transform: rotate(360deg); }
}

/* 按钮加载状态 */
.btn-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid #fff;
  border-radius: 50%;
  animation: spinner 0.8s linear infinite;
  margin-right: 8px;
  vertical-align: middle;
}

@keyframes spinner {
  to { transform: rotate(360deg); }
}

/* ===== 卡片底部 ===== */
.card-footer {
  margin-top: 22px;
  padding-top: 16px;
  border-top: 1px solid rgba(229, 230, 235, 0.3);
  text-align: center;
}

.security-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #86909c;
  font-size: 13px;
}

.pulse-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  background: #00b42a;
  border-radius: 50%;
  animation: pulseDot 2s ease-in-out infinite;
}

@keyframes pulseDot {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.5);
    opacity: 0.5;
  }
}

/* ===== 页脚 ===== */
.footer {
  height: 48px;
  padding: 0 24px;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(12px);
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  z-index: 2;
  position: relative;
  flex-shrink: 0;
}

.footer-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  height: 100%;
}

.author {
  color: #86909c;
  font-size: 13px;
}

.author a {
  color: #1677ff;
  text-decoration: none;
  transition: color 0.2s;
}

.author a:hover {
  color: #4096ff;
  text-decoration: underline;
}

.footer-emoji {
  display: inline-block;
  animation: heartBeat 1.5s ease-in-out infinite;
}

@keyframes heartBeat {
  0%, 100% { transform: scale(1); }
  14% { transform: scale(1.2); }
  28% { transform: scale(1); }
  42% { transform: scale(1.2); }
  70% { transform: scale(1); }
}

/* ===== 响应式 ===== */
@media (max-width: 768px) {
  .header {
    padding: 0 16px;
    height: 52px;
  }
  
  .logo {
    font-size: 14px;
  }
  
  .logo-icon {
    font-size: 18px;
  }
  
  .header-title {
    font-size: 13px;
  }
  
  .login-card {
    padding: 24px 20px 20px;
    margin: 0 12px;
    max-width: 100%;
    border-radius: 16px;
  }
  
  .form-title {
    font-size: 20px;
  }
  
  .avatar-ring {
    width: 64px;
    height: 64px;
  }
  
  .avatar-emoji {
    font-size: 28px;
  }
  
  .footer {
    padding: 0 16px;
    height: 44px;
  }
}

@media (max-width: 480px) {
  .header {
    height: 48px;
    padding: 0 12px;
  }
  
  .login-card {
    padding: 20px 16px 16px;
    border-radius: 14px;
  }
  
  .form-title {
    font-size: 18px;
  }
  
  .styled-input >>> .el-input__inner {
    height: 42px;
    font-size: 13px;
  }
  
  .login-btn, .reset-btn {
    height: 42px;
    font-size: 14px;
  }
  
  .footer {
    height: 40px;
  }
  
  .author {
    font-size: 12px;
  }
  
  .btn-divider {
    padding: 4px 0 6px;
  }
  
  .divider-text {
    font-size: 11px;
  }
}

/* ===== Element-UI 覆盖 ===== */
:deep(.el-loading-mask) {
  background-color: rgba(232, 244, 253, 0.7);
}

:deep(.el-loading-spinner .path) {
  stroke: #1677ff;
}

:deep(.el-form-item.is-error .el-input__inner) {
  border-color: #f53f3f;
}

:deep(.el-form-item__error) {
  color: #f53f3f;
  font-size: 13px;
  padding-top: 4px;
  animation: shakeError 0.4s ease;
}

@keyframes shakeError {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
}

:deep(.el-button+.el-button) {
  margin: 0;
}

:deep(.el-message) {
  border-radius: 12px;
}

:deep(.el-message .el-message__content) {
  font-size: 14px;
}
</style>